import { useState } from 'react';
import { Shield, Clock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/stores/authStore';
import api from '@/lib/api';
import { KYCStatus } from '@hirenfire/shared';
import type { ApiResponse, UserWithProfile } from '@hirenfire/shared';

const statusConfig: Record<KYCStatus, {
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  title: string;
  description: string;
  variant: 'success' | 'warning' | 'error' | 'default' | 'primary';
}> = {
  [KYCStatus.NOT_STARTED]: {
    icon: <Shield className="w-8 h-8" />,
    color: 'text-gray-400',
    bgColor: 'bg-dark-700',
    title: 'Not Verified',
    description: 'Complete KYC verification to unlock all platform features.',
    variant: 'default',
  },
  [KYCStatus.PENDING]: {
    icon: <Clock className="w-8 h-8" />,
    color: 'text-warning-400',
    bgColor: 'bg-warning-500/10',
    title: 'Pending',
    description: 'Your verification request has been submitted and is awaiting review.',
    variant: 'warning',
  },
  [KYCStatus.SCHEDULED]: {
    icon: <Clock className="w-8 h-8" />,
    color: 'text-primary-400',
    bgColor: 'bg-primary-500/10',
    title: 'Scheduled',
    description: 'Your video verification call has been scheduled.',
    variant: 'primary',
  },
  [KYCStatus.IN_REVIEW]: {
    icon: <AlertTriangle className="w-8 h-8" />,
    color: 'text-warning-400',
    bgColor: 'bg-warning-500/10',
    title: 'In Review',
    description: 'Your verification is being reviewed by our team.',
    variant: 'warning',
  },
  [KYCStatus.APPROVED]: {
    icon: <CheckCircle className="w-8 h-8" />,
    color: 'text-success-400',
    bgColor: 'bg-success-500/10',
    title: 'Verified',
    description: 'Your identity has been verified. You have full access to the platform.',
    variant: 'success',
  },
  [KYCStatus.REJECTED]: {
    icon: <XCircle className="w-8 h-8" />,
    color: 'text-error-400',
    bgColor: 'bg-error-500/10',
    title: 'Rejected',
    description: 'Your verification was not approved. You can submit a new request.',
    variant: 'error',
  },
};

export function KYCPage() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const [isLoading, setIsLoading] = useState(false);

  const kycStatus = user?.kyc?.status ?? KYCStatus.NOT_STARTED;
  const config = statusConfig[kycStatus as KYCStatus];
  const canRequest = kycStatus === KYCStatus.NOT_STARTED || kycStatus === KYCStatus.REJECTED;

  const handleRequestVerification = async () => {
    setIsLoading(true);
    try {
      const res = await api.post<ApiResponse<UserWithProfile>>('/kyc/request');
      if (res.data.data) setUser(res.data.data);
    } catch {
      // handle error
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-white">Identity Verification</h1>
        <p className="text-gray-400 mt-1">
          KYC verification is required to access all platform features
        </p>
      </div>

      <Card>
        <CardBody>
          <div className="text-center py-8">
            <div className={`w-20 h-20 mx-auto mb-6 rounded-full ${config.bgColor} flex items-center justify-center ${config.color}`}>
              {config.icon}
            </div>
            <Badge variant={config.variant} className="mb-3">
              {config.title}
            </Badge>
            <h2 className="text-xl font-bold text-white mt-3">{config.title}</h2>
            <p className="text-gray-400 mt-2 max-w-md mx-auto">{config.description}</p>

            {user?.kyc?.scheduledAt && (
              <p className="text-sm text-primary-400 mt-4">
                Scheduled for: {new Date(user.kyc.scheduledAt).toLocaleString()}
              </p>
            )}

            {canRequest && (
              <Button
                className="mt-6"
                size="lg"
                onClick={handleRequestVerification}
                isLoading={isLoading}
              >
                {kycStatus === KYCStatus.REJECTED ? 'Submit New Request' : 'Start Verification'}
              </Button>
            )}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>How it works</CardHeader>
        <CardBody>
          <div className="space-y-4">
            {[
              { step: '1', title: 'Submit Request', desc: 'Click the button above to start the process.' },
              { step: '2', title: 'Video Call', desc: 'A short video call will be scheduled with our verification team.' },
              { step: '3', title: 'ID Check', desc: 'Present a valid government ID during the call.' },
              { step: '4', title: 'Approval', desc: 'Get verified and unlock all platform features.' },
            ].map((item) => (
              <div key={item.step} className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-primary-500/10 text-primary-400 flex items-center justify-center text-sm font-bold flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <p className="font-medium text-white">{item.title}</p>
                  <p className="text-sm text-gray-400">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
